import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ListOrdered, Loader2, Plus, Trash2, GraduationCap, TrendingUp, TrendingDown, Minus } from "lucide-react";

const NAVY = "#0B1B3F";

function evaluate(current, target) {
  const t = Number(target || 0);
  if (t <= 0) return { label: "Hedefsiz", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: Minus };
  if (current >= t) return { label: "Güvenli", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: TrendingUp };
  if (current >= t * 0.85) return { label: "Yaklaşıyor", cls: "bg-amber-100 text-amber-700 border-amber-300", Icon: TrendingUp };
  if (current >= t * 0.7) return { label: "Riskli", cls: "bg-orange-100 text-orange-700 border-orange-300", Icon: Minus };
  return { label: "Uzak", cls: "bg-rose-100 text-rose-700 border-rose-300", Icon: TrendingDown };
}

export default function Preferences({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [avg, setAvg] = useState({ tyt: 0, ayt: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ rank: "", university: "", department: "", exam_type: "TYT", target_net: "", target_rank: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([
        api.get(`/preferences/${studentId}`),
        api.get(`/analytics/overview/${studentId}`),
      ]);
      setItems(p.data);
      setAvg({ tyt: Number(o.data.avg_tyt_net || 0), ayt: Number(o.data.avg_ayt_net || 0) });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.university) return toast.error("Üniversite zorunlu");
    setSaving(true);
    try {
      await api.post(`/preferences/${studentId}`, {
        rank: form.rank ? Number(form.rank) : items.length + 1,
        university: form.university,
        department: form.department,
        exam_type: form.exam_type,
        target_net: Number(form.target_net || 0),
        target_rank: form.target_rank ? Number(form.target_rank) : null,
      });
      toast.success("Tercih eklendi");
      setForm({ rank: "", university: "", department: "", exam_type: "TYT", target_net: "", target_rank: "" });
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/preferences/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const sorted = [...items].sort((a, b) => Number(a.rank || 99) - Number(b.rank || 99));
  const rows = sorted.map((p) => {
    const current = p.exam_type === "AYT" ? avg.ayt : avg.tyt;
    const gap = Math.round((current - Number(p.target_net || 0)) * 100) / 100;
    return { ...p, current, gap, ev: evaluate(current, p.target_net) };
  });
  const counts = rows.reduce((a, r) => ({ ...a, [r.ev.label]: (a[r.ev.label] || 0) + 1 }), {});

  return (
    <div className="space-y-5" data-testid="preferences-panel">
      <div>
        <div className="text-xs font-semibold text-slate-400">YÖK Atlas Tercih Robotu</div>
        <h3 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight mt-1">TYT/AYT Tercih Listesi & Hedef Net</h3>
        <p className="text-sm text-slate-500 mt-1.5">
          Tercihlerini sırala; robot, deneme ortalamana göre her tercihi değerlendirir. Mevcut ortalama: <b className="text-blue-700">TYT {avg.tyt}</b> · <b className="text-amber-600">AYT {avg.ayt}</b>
        </p>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="pref-eval-summary">
          {["Güvenli", "Yaklaşıyor", "Riskli", "Uzak"].map((k) => (
            <span key={k} className={`text-xs font-bold px-3 py-1.5 rounded-full border ${evaluate(k === "Güvenli" ? 100 : k === "Yaklaşıyor" ? 90 : k === "Riskli" ? 75 : 50, 100).cls}`} data-testid={`pref-count-${k}`}>
              {k}: {counts[k] || 0}
            </span>
          ))}
        </div>
      )}

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><GraduationCap size={16} className="text-indigo-600" /> Tercih Ekle</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="ta-label">Sıra</label>
              <input className="ta-input" type="number" min="1" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} placeholder={String(items.length + 1)} data-testid="pref-rank" />
            </div>
            <div className="col-span-2">
              <label className="ta-label">Üniversite</label>
              <input className="ta-input" value={form.university} onChange={(e) => setForm({ ...form, university: e.target.value })} placeholder="Örn: ODTÜ" data-testid="pref-university" />
            </div>
            <div className="col-span-2">
              <label className="ta-label">Bölüm</label>
              <input className="ta-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Bilgisayar Mühendisliği" data-testid="pref-department" />
            </div>
            <div>
              <label className="ta-label">Puan Türü</label>
              <select className="ta-input" value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })} data-testid="pref-exam-type">
                <option value="TYT">TYT</option>
                <option value="AYT">AYT</option>
              </select>
            </div>
            <div>
              <label className="ta-label">Hedef Net</label>
              <input className="ta-input" type="number" step="0.25" value={form.target_net} onChange={(e) => setForm({ ...form, target_net: e.target.value })} data-testid="pref-target-net" />
            </div>
            <div>
              <label className="ta-label">Taban Sıralama</label>
              <input className="ta-input" type="number" value={form.target_rank} onChange={(e) => setForm({ ...form, target_rank: e.target.value })} placeholder="opsiyonel" data-testid="pref-target-rank" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="mt-3 bg-indigo-600 hover:bg-indigo-700" data-testid="pref-add-btn">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Listeye Ekle</span>
          </Button>
        </form>
      )}

      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="text-white px-5 py-3.5 flex items-center justify-between" style={{ background: NAVY }}>
          <span className="font-display font-extrabold tracking-wide flex items-center gap-2"><ListOrdered size={17} /> TERCİH LİSTEM — ROBOT DEĞERLENDİRMESİ</span>
          <span className="text-xs font-bold bg-green-500 text-white px-3 py-1 rounded-full">{rows.length} tercih</span>
        </div>
        <div className="overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium w-14">Sıra</th>
                <th className="px-4 py-2.5 font-medium">Üniversite</th>
                <th className="px-4 py-2.5 font-medium">Bölüm</th>
                <th className="px-4 py-2.5 font-medium">Tür</th>
                <th className="px-4 py-2.5 font-medium text-right">Hedef Net</th>
                <th className="px-4 py-2.5 font-medium text-right">Mevcut Ort.</th>
                <th className="px-4 py-2.5 font-medium text-right">Fark</th>
                <th className="px-4 py-2.5 font-medium text-right">Taban Sıralama</th>
                <th className="px-4 py-2.5 font-medium">Robot Değerlendirmesi</th>
                {!readOnly && <th className="px-4 py-2.5" />}
              </tr>
            </thead>
            <tbody data-testid="pref-table">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Henüz tercih eklenmedi. Yukarıdan ilk tercihini ekle.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pref-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900 text-white font-display font-extrabold text-sm">{r.rank}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{r.university}</td>
                    <td className="px-4 py-3 text-slate-600">{r.department || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${r.exam_type === "TYT" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{r.exam_type}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">{Number(r.target_net || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{r.current.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-bold ${r.gap >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.gap >= 0 ? "+" : ""}{r.gap.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.target_rank ? Number(r.target_rank).toLocaleString("tr-TR") : "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${r.ev.cls}`} data-testid={`pref-status-${r.id}`}>
                        <r.ev.Icon size={13} /> {r.ev.label}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-rose-600" data-testid={`pref-del-${r.id}`}><Trash2 size={15} /></button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
